/**
 * Oda dağılımı: oda başına yetişkin, çocuk sayısı ve etiketli çocuk yaşı (0-17).
 *
 * Otel teklifi ekranındaki oda `fieldset`i ortak bileşene çıkarıldı (26.10.2026, hızlı teklif şartnamesi 3):
 * aynı markup, aynı test kimlikleri (`<önek>-yetiskin-<i>`, `<önek>-cocuk-<i>`, `<önek>-yas-<i>-<k>`). İki ekran
 * da bunu kullanır; üçüncü bir kopya yazılmaz. Doğrulama (yaş 0-17, yetişkin 1-10) çağıran ekranda kalır;
 * bileşen yalnız eksik yaşı `aria-invalid` ile işaretleyebilir.
 */
import { useId, type ReactNode } from 'react';

export interface OdaDagilimiOda {
  readonly id: string;
  readonly yetiskin: number | string;
  readonly cocukYaslari: readonly string[];
  /** Yalnız `ayMetni` verilen ekranda kullanılır: çocukla aynı sırada ay (dize). */
  readonly cocukAylari?: readonly string[];
}

export interface OdaDagilimiMetni {
  readonly oda: (n: number) => string;
  readonly yetiskin: string;
  readonly cocuk: string;
  readonly cocukYasi: (n: number) => string;
  readonly yasSec: string;
  readonly odaKaldir: (n: number) => string;
}

export interface OdaDagilimiAyMetni {
  readonly etiket: (n: number) => string;
  readonly sec: string;
}

export interface OdaDagilimiSiniflari {
  readonly kap?: string;
  readonly oda?: string;
  readonly alanlar?: string;
  readonly yaslar?: string;
  readonly kaldir?: string;
}

const sayilar = (en: number, enAz = 0) => Array.from({ length: en - enAz + 1 }, (_, i) => i + enAz);

export function OdaDagilimi({
  odalar,
  degistir,
  kaldir,
  onek,
  metin,
  siniflar = {},
  devreDisi = false,
  hataliYaslar,
  hataMetniId,
  odaEki,
  ayMetni,
}: {
  odalar: readonly OdaDagilimiOda[];
  /** Oda kimliği ve yama: `yetiskin` ve yaş dizisi dize olarak gelir (select değeri). */
  degistir: (odaId: string, yama: { yetiskin?: string; cocukYaslari?: string[]; cocukAylari?: string[] }) => void;
  kaldir: (odaId: string) => void;
  onek: string;
  metin: OdaDagilimiMetni;
  siniflar?: OdaDagilimiSiniflari;
  devreDisi?: boolean;
  /** "oda:cocuk" (0 tabanlı) biçiminde eksik yaşlar; `aria-invalid` ve açıklama bağı bunlara yazılır. */
  hataliYaslar?: ReadonlySet<string>;
  hataMetniId?: string;
  /** Her odanın başlığının yanına eklenen ek içerik (ör. özet). */
  odaEki?: (oda: OdaDagilimiOda, i: number) => ReactNode;
  /** Verilirse 0 yaşındaki çocuğun yanında isteğe bağlı "ay" seçimi çıkar (hızlı teklif: "4 yaş ve 3 aylık"). */
  ayMetni?: OdaDagilimiAyMetni;
}) {
  const kimlik = useId();
  return (
    <div className={siniflar.kap ?? 'oda-dagilimi'}>
      {odalar.map((oda, i) => {
        const yaslar = oda.cocukYaslari;
        return (
          <fieldset key={oda.id} className={siniflar.oda ?? 'oda-dagilimi-oda'} disabled={devreDisi}>
            <legend>{metin.oda(i + 1)}</legend>
            <div className={siniflar.alanlar ?? 'oda-dagilimi-alanlar'}>
              <label>
                {metin.yetiskin}
                <select data-testid={`${onek}-yetiskin-${i}`} value={String(oda.yetiskin)} onChange={(e) => degistir(oda.id, { yetiskin: e.target.value })}>
                  {sayilar(10, 1).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label>
                {metin.cocuk}
                <select
                  data-testid={`${onek}-cocuk-${i}`}
                  value={yaslar.length}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    degistir(oda.id, { cocukYaslari: Array.from({ length: n }, (_, k) => yaslar[k] ?? '') });
                  }}
                >
                  {sayilar(10).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              {odalar.length > 1 ? (
                <button type="button" className={siniflar.kaldir} data-testid={`${onek}-oda-kaldir-${i}`} aria-label={metin.odaKaldir(i + 1)} onClick={() => kaldir(oda.id)}>-</button>
              ) : null}
            </div>
            {yaslar.length > 0 ? (
              <div className={siniflar.yaslar ?? 'oda-dagilimi-yaslar'}>
                {yaslar.map((yas, k) => {
                  const hata = hataliYaslar?.has(`${i}:${k}`) === true;
                  return (
                    <label key={`${kimlik}-${k}`}>
                      {metin.cocukYasi(k + 1)}
                      <select
                        data-testid={`${onek}-yas-${i}-${k}`}
                        value={yas}
                        aria-invalid={hata ? 'true' : undefined}
                        aria-describedby={hata ? hataMetniId : undefined}
                        onChange={(e) => degistir(oda.id, { cocukYaslari: yaslar.map((y, j) => (j === k ? e.target.value : y)) })}
                      >
                        <option value="">{metin.yasSec}</option>
                        {sayilar(17).map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                  );
                })}
                {ayMetni === undefined ? null : yaslar.map((yas, k) => (yas === '0' ? (
                  <label key={`${kimlik}-ay-${k}`}>
                    {ayMetni.etiket(k + 1)}
                    <select
                      data-testid={`${onek}-ay-${i}-${k}`}
                      value={oda.cocukAylari?.[k] ?? ''}
                      onChange={(e) => degistir(oda.id, { cocukAylari: yaslar.map((_, j) => (j === k ? e.target.value : (oda.cocukAylari?.[j] ?? ''))) })}
                    >
                      <option value="">{ayMetni.sec}</option>
                      {sayilar(11, 1).map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                ) : null))}
              </div>
            ) : null}
            {odaEki?.(oda, i)}
          </fieldset>
        );
      })}
    </div>
  );
}
